<script lang="ts">
import {type BarChannel, channelPercent, shareOf} from '@/constants/statBarChannel';

/**
 * Where the slice sits on the bar, as percentages of the bar's full width.
 *
 * Pure and exported so a spec can check the arithmetic without mounting the
 * component — the same reason `resolveActionTriggerMode` lives outside its own
 * `<script setup>`.
 *
 * `visible` is false when there is nothing to say: no cost, no scale, or a bar
 * already at zero. `short` means the cost outruns the pool, in which case the
 * slice runs from empty to whatever is there — it takes everything and it is
 * still not enough.
 */
export function resolveDrain(
    current: number,
    max: number,
    cost: number,
    channel: BarChannel,
): {
  visible: boolean;
  short: boolean;
  left: number;
  width: number;
} {
  // Measured against the bar's drawable channel, not its element — see
  // constants/statBarChannel.ts. Against the element, a small cost on the
  // Action Points bar lands past the art entirely and draws nothing.
  const at = (value: number) => channelPercent(channel, shareOf(value, max));

  const left = at(Math.max(0, current - cost));
  return {
    visible: cost > 0 && max > 0 && current > 0,
    short: cost > current,
    left,
    width: Math.max(0, at(current) - left),
  };
}
</script>

<script setup lang="ts">
import {computed} from 'vue';
import {RESOURCE_RGB, type ResourceTone} from '@/constants/resourceTone';
import {STAT_BAR_CHANNEL} from '@/constants/statBarChannel';

/**
 * What a pending action would take out of one resource bar.
 *
 * The slice between the level the bar will drop to and the level it stands at
 * now is hatched out, and a lit rule marks the boundary. The rule is the
 * information: a player choosing a skill mid-fight is asking "what am I left
 * with", not "what does this cost" — the number is already in the caption.
 *
 * Renders inside the bar's masked wrapper and BEFORE the fill, which is what
 * puts it over the fill art and still under the `current / max` label without
 * anyone having to stack z-indexes.
 */

interface Props {
  current: number;
  max: number;
  cost: number;
  tone: ResourceTone;
}

const props = defineProps<Props>();

const drain = computed(() =>
    resolveDrain(props.current, props.max, props.cost, STAT_BAR_CHANNEL[props.tone]));
</script>

<template>
  <div
      v-if="drain.visible"
      class="drain"
      :class="{ 'drain--short': drain.short }"
      :style="{ '--drain-rgb': RESOURCE_RGB[tone], left: drain.left + '%', width: drain.width + '%' }"
      aria-hidden="true"
  >
    <span class="drain__waterline"></span>
  </div>
</template>

<style scoped>
/* No z-index on purpose. The `current / max` label is a positioned descendant
   further down the bar's tree, so plain paint order puts it above this shadow —
   any z-index here would lift the shadow over the numbers instead. */
.drain {
  position: absolute;
  top: 0;
  bottom: 0;
  pointer-events: none;
  /* A shadow cast into the stretch about to be lost: deepest where it meets
     what remains, thinning away from it. The fills are bright photographic
     textures and stay legible underneath — the bar is dimmed, not covered. */
  background: linear-gradient(
      90deg,
      rgba(0, 0, 0, 0.54) 0%,
      rgba(0, 0, 0, 0.42) 55%,
      rgba(0, 0, 0, 0.28) 100%
  );
  transition: left 0.18s ease-out, width 0.18s ease-out;
}

/* The one lit edge on the bar, and the only thing here that moves. */
.drain__waterline {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 2px;
  background: rgb(var(--drain-rgb));
  box-shadow:
      0 0 6px rgba(var(--drain-rgb), 0.95),
      0 0 14px rgba(var(--drain-rgb), 0.5);
  animation: drain-breathe 1.7s ease-in-out infinite;
}

/* Pinned to empty, so the red is the whole message: nothing survives this. */
.drain--short {
  background:
      repeating-linear-gradient(
          115deg,
          rgba(46, 4, 8, 0) 0 3px,
          rgba(46, 4, 8, 0.72) 3px 6px
      ),
      rgba(28, 3, 6, 0.5);
}

.drain--short .drain__waterline {
  background: #ff5c5c;
  box-shadow:
      0 0 7px rgba(255, 92, 92, 0.95),
      0 0 16px rgba(255, 92, 92, 0.55);
}

@keyframes drain-breathe {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.55;
  }
}

@media (prefers-reduced-motion: reduce) {
  .drain {
    transition: none;
  }

  .drain__waterline {
    animation: none;
  }
}
</style>
